import { useQueryClient } from '@tanstack/react-query';
import { useEntityForm } from '@/shared/lib/useEntityForm';
import { ErrorList } from '@/shared/ui/ErrorList';
import { FilterDateTime } from '@/shared/ui/FilterDateTime';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import { TextField } from '@/shared/ui/TextField';
import type { SelectOption } from '@/shared/ui/selectTypes';
import { editPartnerPromotion } from './api';

const STATUS_OPTIONS: SelectOption[] = [
  { value: '0', label: 'Неактивная' },
  { value: '1', label: 'Отправлена на модерацию' },
  { value: '2', label: 'Модерация прошла успешно' },
  { value: '3', label: 'Модерация отклонена' },
  { value: '4', label: 'Акция остановлена' },
  { value: '5', label: 'Акция запущена' },
  { value: '6', label: 'Акция завершена' },
  { value: '7', label: 'Акция отправлена в архив' },
];

const PATTERN_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const FIELD_WIDTH = 250;

type PromoActionRow = {
  id: number;
  service_id?: number;
  title?: string;
  description?: string;
  stop_message?: string | null;
  reject_message?: string | null;
  image_id?: number | string;
  city?: { id?: number; name?: string } | null;
  status_id?: number;
  tariff?: { id?: number; type?: string } | null;
  start_time?: number;
};

type FormValues = {
  title: string;
  description: string;
  statusId: string;
  startTime: Date | null;
  stopMessage: string;
  rejectMessage: string;
  imageId: number;
};

type Props = {
  promo: PromoActionRow;
  onClose: () => void;
};

const toValues = (promo: PromoActionRow): FormValues => ({
  title: promo.title ?? '',
  description: promo.description ?? '',
  statusId: String(promo.status_id ?? ''),
  startTime: promo.start_time ? new Date(promo.start_time * 1000) : null,
  stopMessage: promo.stop_message ?? '',
  rejectMessage: promo.reject_message ?? '',
  imageId: Number(promo.image_id) || 1,
});

export const PromoActionEditModal = ({ promo, onClose }: Props) => {
  const qc = useQueryClient();
  const { values, errors, setField, save, isSaving } = useEntityForm(
    toValues(promo),
    (v: FormValues) =>
      editPartnerPromotion({
        id: promo.id,
        serviceId: promo.service_id ?? '',
        statusId: v.statusId,
        title: v.title,
        description: v.description,
        imageId: v.imageId,
        cityId: promo.city?.id ?? '',
        startTime: v.startTime ? Math.floor(v.startTime.getTime() / 1000) : 0,
        tariffId: promo.tariff?.id ?? '',
        tariffType: promo.tariff?.type ?? '',
        stopMessage: v.stopMessage,
        rejectMessage: v.rejectMessage,
      }),
    () => {
      void qc.invalidateQueries({ queryKey: ['marketing-promo-actions'] });
      onClose();
    },
  );

  const statusNum = Number(values.statusId);

  return (
    <ModalDialog title="Редактировать промоакцию" onClose={onClose} onSave={save} isSaving={isSaving}>
      <TextField
        label="Title"
        placeholder="Название..."
        maxLength={36}
        maxWidth={FIELD_WIDTH}
        value={values.title}
        errors={errors.title}
        onChange={(v) => setField('title', v)}
      />
      <FormField label="Description" errors={errors.description}>
        <textarea
          className="form-control"
          rows={3}
          maxLength={160}
          placeholder="Описание..."
          value={values.description}
          onChange={(e) => setField('description', e.target.value)}
        />
      </FormField>
      <FormField label="Время начала акции" errors={errors.start_time}>
        <FilterDateTime
          width={FIELD_WIDTH}
          value={values.startTime}
          placeholder="Время начала акции"
          onChange={(date) => setField('startTime', date, 'start_time')}
        />
      </FormField>
      <FormField label="Статус акции" errors={errors.status_id}>
        <FilterSelect
          width={FIELD_WIDTH}
          options={STATUS_OPTIONS}
          value={values.statusId}
          placeholder="Выберите статус акции"
          isClearable={false}
          onChange={(v) => setField('statusId', v, 'status_id')}
        />
      </FormField>
      <FormField label="Причина остановки заявки" errors={errors.stop_message}>
        <textarea
          className="form-control"
          rows={2}
          disabled={statusNum !== 4}
          placeholder="Описание..."
          value={values.stopMessage}
          onChange={(e) => setField('stopMessage', e.target.value, 'stop_message')}
        />
      </FormField>
      <FormField label="Причина отклонения заявки" errors={errors.reject_message}>
        <textarea
          className="form-control"
          rows={2}
          disabled={statusNum !== 3}
          placeholder="Описание..."
          value={values.rejectMessage}
          onChange={(e) => setField('rejectMessage', e.target.value, 'reject_message')}
        />
      </FormField>
      <FormField label="Паттерн" errors={errors.image_id}>
        <div className="promo-action-patterns">
          {PATTERN_IDS.map((id) => (
            <label key={id} className="promo-action-pattern">
              <input
                type="radio"
                name="promo-pattern"
                checked={values.imageId === id}
                onChange={() => setField('imageId', id, 'image_id')}
              />
              <span
                className="promo-action-pattern-img"
                style={{
                  backgroundImage: `url(https://uremont-main.s3.eu-central-1.amazonaws.com/img/promo/promo-${id}.jpg)`,
                }}
              />
            </label>
          ))}
        </div>
      </FormField>
      <ErrorList errors={{ common: errors.common ?? [], server: errors.server ?? [] }} />
    </ModalDialog>
  );
};
